import { Fragment, useEffect, useState } from 'react'
import type { AccessHierarchyPreview, ApiFailure } from '../api/types'
import type { Api } from '../api'
import { Button, Loading } from '../components/ui'
import { SlideOver } from '../components/SlideOver'
import { FailureState } from '../components/FailureState'
import { Verdict, dayTime, failureOf } from './accessWords'

/**
 * THE HIERARCHY PREVIEW (D49; owner, 28 Oct 2026): before a role's hierarchy goes
 * on, what each holder's rows become, per report: now, with Type 1 and with Type 2.
 * Counts only, never rows or money ("Rows per report, now vs after"), the last 12
 * months from the daily lists, most staff first. Read-only: it changes nothing, and
 * the role's own Review shows what a save does.
 */
export function HierarchyPreview({ api, roleId, roleName, onClose }: {
  api: Api
  roleId: number
  roleName: string
  onClose: () => void
}) {
  const [read, setRead] = useState<AccessHierarchyPreview | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [retry, setRetry] = useState(0)
  useEffect(() => {
    let cancelled = false
    setRead(null)
    setFailure(null)
    api.accessHrRoleHierarchyPreview(roleId)
      .then((r) => { if (!cancelled) setRead(r) })
      .catch((e: unknown) => { if (!cancelled) setFailure(failureOf(e)) })
    return () => { cancelled = true }
  }, [api, roleId, retry])

  const people = read ? [...new Map(read.preview.map((r) => [r.mindzerUserId, r])).values()] : []
  return (
    <SlideOver
      width="lg"
      title="What hierarchy would change"
      description={<>{roleName}: each holder&apos;s rows per report, the last 12 months, with no hierarchy, with Type 1 and with Type 2. Counts only; nothing is changed.</>}
      onClose={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      {failure ? (
        <FailureState failure={failure} onRetry={() => setRetry((n) => n + 1)} />
      ) : !read ? (
        <Loading label="Counting each holder's rows…" />
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-(length:--text-small) leading-relaxed text-(--color-ink-2)">
            <strong className="font-semibold text-(--color-ink)">Type 1</strong> keeps only rows whose salesperson is them or someone under them.{' '}
            <strong className="font-semibold text-(--color-ink)">Type 2</strong> adds what each user under them sees in their own scope:
            cut the same way when that user&apos;s hierarchy is on, all of it when it is off.
            Material Planning is counted by its planning rows and Inventory Aging by its stock; hierarchy never changes either.
            Material Planning&apos;s sales figures follow hierarchy as the Sales report does.
          </p>
          <p className="text-(length:--text-small) text-(--color-ink-3)">
            {read.holders === null ? '' : `${read.holders.toLocaleString('en-GB')} ${read.holders === 1 ? 'user holds' : 'users hold'} this role`}
            {read.holders !== null && people.length < read.holders ? `; the ${people.length} with the most staff are shown` : ''}
            {read.countedUtc ? `. Counted ${dayTime(read.countedUtc)}.` : '.'}
          </p>
          {!read.countedUtc && (
            <Verdict ok={null} tone="warn" title="Salespeople have not been counted yet">
              With Type 1 and With Type 2 read 0 until the daily job has run once.
            </Verdict>
          )}
          {read.preview.length === 0 ? (
            <Verdict ok={null} title="Nobody holds this role with a report yet">
              Nothing would change: nobody who holds it opens a report.
            </Verdict>
          ) : (
            <div className="overflow-x-auto rounded-md border border-(--color-line)">
              <table className="w-full border-collapse text-(length:--text-small)">
                <thead className="bg-(--color-surface-2) text-left text-(--color-ink-3)">
                  <tr>
                    <th className="whitespace-nowrap px-3 py-2 font-medium">User</th>
                    <th className="whitespace-nowrap px-3 py-2 font-medium">Report</th>
                    {/* "Now" only while the role is off; the person's "Today" line says which column they see. */}
                    <th className="whitespace-nowrap px-3 py-2 text-right font-medium">No hierarchy</th>
                    <th className="whitespace-nowrap px-3 py-2 text-right font-medium">With Type 1</th>
                    <th className="whitespace-nowrap px-3 py-2 text-right font-medium">With Type 2</th>
                  </tr>
                </thead>
                <tbody>
                  {people.map((p) => {
                    const rows = read.preview.filter((r) => r.mindzerUserId === p.mindzerUserId)
                    return (
                      <Fragment key={p.mindzerUserId}>
                        {rows.map((r, i) => (
                          <tr key={r.reportSlug} className={i === 0 ? 'border-t border-(--color-line-strong)' : 'border-t border-(--color-line)'}>
                            {i === 0 && (
                              <td rowSpan={rows.length} className="align-top px-3 py-2">
                                <div className="font-medium text-(--color-ink)">{p.displayName ?? `Mindzer user ${p.mindzerUserId}`}</div>
                                <div className="text-(--color-ink-3)">
                                  {[p.compId, p.staffAll === 0 ? 'nobody under them' : `${p.staffAll.toLocaleString('en-GB')} under them (${p.staffDirect} direct)`]
                                    .filter(Boolean).join(' · ')}
                                </div>
                                {(p.modeNow > 0 || p.ownSetting) && (
                                  <div className="mt-0.5 text-(length:--text-micro) text-(--color-ink-3)">
                                    {p.modeNow > 0 ? `Today: Type ${p.modeNow}` : 'Today: no hierarchy'}
                                    {p.ownSetting ? ' (their own override: changing the role does not change it)' : ''}
                                  </div>
                                )}
                              </td>
                            )}
                            <td className="whitespace-nowrap px-3 py-2 text-(--color-ink-2)">{r.reportName}</td>
                            <td className="whitespace-nowrap px-3 py-2 text-right font-medium tabular-nums text-(--color-ink)">{r.rowsNow.toLocaleString('en-GB')}</td>
                            <Change now={r.rowsNow} then={r.rowsType1} applies={r.hierarchyApplies} />
                            <Change now={r.rowsNow} then={r.rowsType2} applies />
                          </tr>
                        ))}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </SlideOver>
  )
}

/** A count after the change, with how it moves from now. */
function Change({ now, then, applies }: { now: number; then: number; applies: boolean }) {
  const diff = then - now
  return (
    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
      <span className="font-medium text-(--color-ink)">{then.toLocaleString('en-GB')}</span>
      {!applies ? (
        <span className="ml-1.5 text-(length:--text-micro) text-(--color-ink-4)" title="This report has no salesperson: hierarchy does not cut it.">same</span>
      ) : diff !== 0 ? (
        <span className={`ml-1.5 text-(length:--text-micro) font-semibold ${diff < 0 ? 'text-(--color-overdue)' : 'text-(--color-good)'}`}>
          {diff < 0 ? '−' : '+'}{Math.abs(diff).toLocaleString('en-GB')}
        </span>
      ) : null}
    </td>
  )
}
